import type { PreSpawnEffect } from "../pre_spawn_effect"
/** Content contracts are independent of models, mechanics and platform IO. */
export type ItemId = string
export type ColorId = string
export type RaceId = string
export type SpriteComponentId = string
export interface ItemReference { item: ItemId; color?: ColorId }
export interface ItemMetadata { type: ItemId; class: string; color: ColorId; spriteId?: string; noGlint?: boolean }
export interface ItemDefinition {
    id: ItemId
    class: string
    subClass: string
    metadata?: ItemMetadata[]
    sprite?: string
    weight?: number | null
    protection?: number | null
    characteristics?: Record<string, unknown>
    trade?: { itemType: ItemId; quantity: number }
}
export interface RecipeDefinition {
    /** Preserved authoring hint; legacy mechanics never enforced this field. */
    legacySkills?: string
    output: ItemReference
    quantity: number
    ingredients: { reference: ItemReference; quantity: number }[]
}
export interface SpriteLayer { z: number; path: string; type?: "primary" | "oversize" }
export interface SpriteEntry {
    colors?: ColorId[]
    layers?: SpriteLayer[]
    variants?: Record<ColorId, string>
    explicit?: Record<string, SpriteLayer[]>
    shadow?: { feetY: number }
    placement?: { x: number; y: number }
    /** Stable ground contact in native body-frame pixels. */
    groundAnchor?: { x: number; y: number }
    /** Positive values sort in front sooner; independent of art translation. */
    depthBias?: number
}
export interface CreatureProfile {
    preSpawnEffect?: PreSpawnEffect | null
    template: RaceId
    hp?: number; speed?: number; hostility?: number; faction?: string; scanDistanceTiles?: number
    drops?: { item: ItemId; quantity: number; probability: number }[]
}
export type EngineSex = "male" | "female"
export type EngineAction = "walk" | "idle" | "thrust" | "slash" | "shoot" | "hurt" | "activate" | "deactivate" | "active" | "inactive"
export interface SpriteRegistration {
    HumanoidRaceTypes: RaceId[]; RaceTypes: RaceId[]; RaceSexes: Record<RaceId, EngineSex[]>
    EyeTypes: string[]; NoseTypes: string[]; RaceNoses: Record<RaceId, string[]>; RaceEyes: Record<RaceId, string[]>
    WeaponTypes: string[]; WeaponAction: Record<string, EngineAction>; WeaponAmmo: Record<string, ItemId>
    RaceWeapons: Record<RaceId, string[]>; DefaultAttackActionType: Record<RaceId, EngineAction>
    HairTypes: string[]; SexHair: Record<EngineSex, string[]>
    PantsTypes: string[]; SexPants: Record<EngineSex, string[]>
    ShirtTypes: string[]; SexShirts: Record<EngineSex, string[]>
    ArmorTypes: string[]; ShoeTypes: string[]; SexShoes: Record<EngineSex, string[]>
    ShieldTypes: string[]; CapeTypes: string[]; SexCapes: Record<EngineSex, string[]>
}
export interface SpriteDefinitions {
    version: 1
    registration: SpriteRegistration
    dimensions: Record<string, { w: number; h: number }>
    creatures: Record<RaceId, CreatureProfile>
    weapons: Record<string, string>
}
export interface TileDefinition {
    hp: number
    /** Decorative entity tiles are rendered upright but cannot be damaged or harvested. */
    interactive?: boolean
    onDestructionCreateItems: { itemType: ItemReference; quantity: number; probability: number; replicate: boolean }[]
    growthSequence: { entityTileId: string; requiredTime: number }[]
    /** A full tree ("grown") or a growth stage on the way to one ("young"); untagged tiles are not trees. */
    tree?: TreeKind
    /** Grass: "long" bends in the wind, is wildlife cover, is cut not chopped and its seeds share one
     *  stack; "sprout" is long grass's young shoot -- it bends a little and is cut, nothing more. */
    grass?: GrassKind
}
export type TreeKind = "grown" | "young"
export const TREE_KINDS: readonly TreeKind[] = ["grown", "young"]
export type GrassKind = "long" | "sprout"
export const GRASS_KINDS: readonly GrassKind[] = ["long", "sprout"]
export type EngineBaseline = "speed" | "hp" | "hostility" | "tileSize"
export type EngineScalar = number | { baseline: EngineBaseline; pctOfMax?: number; multiplier?: number }
export interface PopulationProfile {
    preSpawnEffect?: PreSpawnEffect | null
    maxSpeed: EngineScalar
    hp: EngineScalar
    psych: Record<string, unknown>
    scanDistanceTiles?: number
}
export interface ProfileDrop { reference?: ItemReference; metadata?: ItemMetadata; quantity: number; probability?: number }
export interface EncounterProfile {
    race: RaceId; probability: number; sex?: EngineSex; bodyColor?: ColorId
    hp?: EngineScalar; speed?: EngineScalar; faction?: string; immoveable?: boolean
    flags?: Record<string, boolean | number | string>; brainProps?: Record<string, unknown>
}
export interface ProfileCatalog {
    encounters: Record<string, {
        specs: Record<RaceId, EncounterProfile>; enemyWeights: { race: RaceId; weight: number }[]; defaultEnemy: RaceId
        /** Tuning dials for the code that grows the table, by name (the night forest's caps and thresholds). */
        night?: Record<string, number>
    }>
    weapons: Record<string, Record<string, unknown>>
    populationDefault: PopulationProfile
    population: Record<RaceId, PopulationProfile>
    npcSpecs: Record<RaceId, { npc: Record<string, unknown>; brainProps: Record<string, unknown> }>
    factions: Record<RaceId, string>
    drops: Record<RaceId, ProfileDrop[]>
    itemDrops: Record<ItemId, ProfileDrop[]>
    effects: Record<ItemId, { effect: string; clockDuration?: number }>
}
/**
 * Every map file has one owner. A room's `.tmj` is authoritative; a Tiled `.tmx` it was once exported
 * from is provenance only. Generator inputs feed offline tools; historical files are kept, never loaded.
 */
export interface MapCatalog {
    templates: { id: string; file: string }[]
    tilesets: { id: string; file: string; format: "tsx" | "json"; legacyOmittedTiles?: number[] }[]
    collisionOverrides: Record<string, import("../tile_blocking_geometry").TileBlockingRect[]>
    /** `id` is the room's `roomId` property, which teleports and triggers use; it need not match the file name. */
    rooms: { id: string; file: string; provenance?: string }[]
    world: string
    generatorInputs: { id: string; file: string }[]
    historical: { file: string; note: string }[]
}
/** Renamed and retired identifiers, so saved data and scripts written against older content resolve. */
export interface IdLifecycle {
    /** Old ID -> current ID. */
    aliases: { items: Record<ItemId, ItemId>; races: Record<RaceId, RaceId>; colors: Record<ColorId, ColorId> }
    /** Removed IDs and why; saved copies are set aside, never converted. */
    retired: { items: Record<ItemId, string>; races: Record<RaceId, string>; colors: Record<ColorId, string> }
}
/**
 * One recipe's words on the crafting bench. `family` + `variant` fold it into a shared tile (Wall: Wood /
 * Timber frame / Castle). Its name is the item's display name (`presentation.displayNames`), never its own.
 */
export interface CraftingEntry {
    description?: string
    family?: string
    variant?: string
}
export interface ContentSource {
    ids: IdLifecycle
    maps: MapCatalog
    profiles: ProfileCatalog
    vocabularies: { colors: ColorId[] }
    classes: { id: string; characteristics?: Record<string, unknown> }[]
    items: ItemDefinition[]
    recipes: RecipeDefinition[]
    presentation: {
        /**
         * `undisclosedSources`: materials a recipe card names but never explains -- no hint, no jump to a recipe; players find them out by playing.
         * `entries`: what a recipe is called and says on the crafting bench, keyed by its output `item` or `item:color` (the colour wins).
         * `families`: tiles that fold several recipes into one (every wall, both daggers), by family id -> the tile's name.
         */
        crafting: { groups: string[]; labels: Record<string, string>; overrides: Record<ItemId, string>; byClass: Record<string, string>; defaultGroup: string; sourceHints: Record<ItemId, string>; undisclosedSources?: ItemId[]; entries?: Record<string, CraftingEntry>; families?: Record<string, string> }
        /**
         * What the UI calls each item, everywhere it names one: pack, crafting, trade, hover text, messages
         * ("Roof tile, wood" for roof-wood). Keyed by `item`, or by `item:color` where a colour is a
         * different thing to the player (dagger:brown is "Dagger, wood"); the colour wins. Every item
         * needs one -- its own, or one for each of its colours.
         */
        displayNames: Record<string, string>
        sheets: { id: string; file: string }[]
        /** Migration ordering; new records are appended by the resolver. */
        subClasses: Record<string, ItemId[]>
        classToTypes: Record<string, ItemId[]>
    }
    tiles: {
        types: string[]
        definitions: Record<string, TileDefinition>
        damageDefaults: Record<string, number>
        damageSpecifics: Record<string, { weaponType: string; damage: number }[]>
    }
    registry: Record<string, Record<SpriteComponentId, SpriteEntry>>
    sprites: SpriteDefinitions
    preload: string[]
    /** Terrain and wall autotile definitions. Their shape and checks belong to room/autotile_validation.ts. */
    autotiles: { about?: string; version: 1; definitions: unknown[] }
    /** Ember humanoid equipment: models (what a garment or weapon is made of) and designs (item -> model). See ember_sprite/model.ts. */
    ember: { about?: string; version: 1; library?: unknown[]; models: Record<string, Record<string, unknown>>; designs: { id: string; slot: string; model: string; [field: string]: unknown }[] }
    /** Animations made as data: performances any Ember body plays when asked (ember_sprite/animations.ts EmberAnimationFile). */
    emberAnimations: { about?: string; version: 1; animations: Record<string, { about?: string; frames: number; frameMs: number; loop?: boolean; keys: Record<string, unknown>[] }> }
    /** The Ember body as models: flesh, bones, a beast, wounds, and the body's own volumes (ember_sprite/model.ts EmberBodyFile). */
    emberBodies: { about?: string; version: 1; library?: unknown[]; models: Record<string, unknown> }
    /** Ember hair: its models and the game's styles (ember_sprite/model.ts EmberHairFile). */
    emberHair: { about?: string; version: 1; library?: unknown[]; default: string; none: string[]; models: Record<string, unknown>; styles: Record<string, Record<string, unknown>> }
    /** The named Ember humanoids, their builds and which draws each race (ember_sprite/humanoids.ts EmberHumanoidFile). */
    emberHumanoids: { about?: string; version: 1; builds: Record<string, unknown>; humanoids: { name: string; [field: string]: unknown }[]; races: { race: string; sex?: string; body: string }[]; bodyColors: { skins: Record<string, string>; furs: Record<string, Record<string, string>> } }
    /** Muscle marks and eyes stamped on the render (ember_sprite/marks.ts EmberMarkFile, face.ts EmberEyeFile). */
    emberMarks: { about?: string; version: 1; markLetters: Record<string, string>; markSets: Record<string, unknown>; eyeLetters: Record<string, string>; eyePlacement: Record<string, unknown>; defaultEyes: string; eyeStyles: Record<string, unknown> }
    /** Ember's colours: skin, fur and bone ramps, eye colours, dyes, hair colours (ember_sprite/palette.ts EmberPaletteFile). */
    emberPalettes: { about?: string; version: 1; ramps: Record<string, { about?: string; tones: Record<string, string[]> }>; skins: Record<string, string[]>; eye: { colors: Record<string, string> }; dyes: { colors: Record<string, string> }; hair: { colors: Record<string, string> } }
    /** Species on rigs of their own: joints, legs, gaits, clips, actions, bodies and their models (ember_sprite/species.ts EmberSpeciesFile). */
    emberSpecies: { about?: string; version: 1; library?: unknown[]; models: Record<string, unknown>; species: Record<string, { bodies: Record<string, unknown>; [field: string]: unknown }> }
    /** How a humanoid is drawn: raster, skins, marks, eyes, clips by style (ember_sprite/styles.ts EmberStyleFile). */
    emberStyles: { about?: string; version: 1; default: string; styles: Record<string, { skins: string; [field: string]: unknown }> }
}
export interface ContentManifest {
    schemaVersion: 1
    inputs: { [K in keyof ContentSource]: string }
    /** Published with the client release. */
    media: { source: string; destination: string }[]
    /** Published only to the server release (rooms and world metadata). */
    server: { source: string; destination: string }[]
    /** Registered and hashed for review, never published: editor provenance, generator inputs, history. */
    sources: string[]
}
/** The server-only half of a release, beside the catalog at `runtime/server/<revision>/index.json`. */
export interface ServerRelease {
    schemaVersion: 1
    revision: string
    /** Destinations are relative to the release directory. */
    rooms: { id: string; file: string; width: number; height: number; tilesets: { tilesetId: string; firstGid: number }[] }[]
    world: string
}
export interface RuntimeCatalog {
    /** Published paths by legacy-relative destination; renderer base URLs remain external. */
    assets: Record<string, string>
    mapTemplates: Record<string, any>
    mapTilesets: Record<string, any>
    schemaVersion: 1
    revision: string
    source: ContentSource
    items: {
        all: ItemId[]
        classes: string[]
        classToTypes: Record<string, ItemId[]>
        subClasses: Record<string, ItemId[]>
        metadata: Record<ItemId, ItemMetadata[]>
        resolvedMetadata: Record<ItemId, ItemMetadata[]>
        sprites: Record<ItemId, string>
        weight: Record<string, Record<ItemId, number>>
        protection: Record<string, Record<ItemId, number>>
        characteristics: Record<ItemId, Record<string, unknown>>
        classCharacteristics: Record<string, Record<string, unknown>>
        trade: Record<ItemId, { itemType: ItemId; quantity: number }>
        recipes: { itemTypeMeta: ItemMetadata; quantity: number; ingredients: { itemTypeMeta: ItemMetadata; quantity: number }[] }[]
    }
    npcDrops: Record<RaceId, { itemType: ItemMetadata; quantity: number; probability?: number }[]>
    itemDrops: Record<ItemId, { itemType: ItemMetadata; quantity: number; probability?: number }[]>
    spriteColors: Record<string, Record<string, ColorId[]>>
    creatures: Record<RaceId, CreatureProfile>
    weapons: Record<string, string>
    tiles: Record<string, Omit<TileDefinition, "onDestructionCreateItems"> & {
        onDestructionCreateItems: { itemType: ItemMetadata; quantity: number; probability: number; replicate: boolean }[]
    }>
}
export interface Finding { path: string; message: string }
